/**
 * Security class: the round a position belongs to, read off its name.
 *
 * `securityType` cannot serve. It is whatever sits inside the parentheses of the API's decorated
 * position string, which for most clients is the instrument — "Equity", "Debt" — so it puts every
 * Series A, A-1 and B in one band. The class is what a reader traces a position by, so it is parsed
 * out of the name and the instrument is left alone.
 *
 * This is pattern-matching on display strings, which is fragile by nature. It is bounded to the
 * shapes the ledger actually emits, and anything it cannot place keeps its original text and sorts
 * with the other unplaced classes rather than being guessed into a round.
 */

export type SecurityClassKind = 'preSeed' | 'seed' | 'series' | 'common' | 'safe' | 'note' | 'other';

export interface ISecurityClass {
  /** The specific class, e.g. "Series A-1 Preferred". Identifies a security, not a band. */
  label: string;
  /**
   * The band a row sits in — the round, not the tranche. Series A-1 and Series A share "Series A",
   * which is what the design draws: one band per round with its tranches as rows inside it.
   */
  groupLabel: string;
  /** Chain-chip identifier — the tranche alone, e.g. "A-1", "E", "Note". */
  shortLabel: string;
  kind: SecurityClassKind;
  /** Position in the round sequence; lower sorts first. Tranches of one round share it. */
  rank: number;
}

/**
 * Round sequence: pre-seed, seed, the lettered series, then common. SAFEs and convertible notes sit
 * at the bottom whatever their date — they are pre-round instruments and the design lists them last.
 * Anything unplaced sits above them, so an unrecognised class never displaces a real round.
 */
const Rank = {
  preSeed: 1_000,
  seed: 2_000,
  series: 3_000,
  common: 900_000,
  other: 950_000,
  safe: 970_000,
  note: 980_000,
} as const;

const PreSeedPattern = /pre[\s-]?seed/i;
const SafePattern = /\bsafes?\b|simple agreement for future equity/i;
const NotePattern = /convertible\s+note|conv\.?\s*note|promissory\s+note|\bnotes?\b/i;
const SeedPattern = /\bseed\b/i;
/**
 * The sub-tranche is only ever read from a hyphenated suffix. Allowing a space would read the year in
 * "Series A 2022-09" as a tranche and file that lot under a "Series A-20" of its own.
 */
const SeriesPattern = /series\s+([a-z]{1,2})(?:-(\d{1,2}))?(?![a-z0-9])/i;
const CommonPattern = /\bcommon\b/i;
const PreferredPattern = /\bpref/i;

/** The label shown when a class cannot be placed and there is no text to fall back on. */
export const UnclassifiedLabel = 'Other';

/**
 * The class of the first text that names one. Callers pass the security's own name first and its
 * type second: the name carries the round ("Series A-1 Pref 2022-09"), the type usually does not.
 *
 * Nothing recognisable leaves the class `other`, labelled with the first non-empty text so the band
 * still says something a reader can match against the row.
 */
export function getSecurityClass(...texts: (string | null | undefined)[]): ISecurityClass {
  for (const text of texts) {
    const parsed = text == null ? null : parseClass(text);
    if (parsed !== null) {
      return parsed;
    }
  }
  const fallback = texts.find((text) => text != null && text.trim() !== '')?.trim();
  return {
    label: fallback ?? UnclassifiedLabel,
    groupLabel: fallback ?? UnclassifiedLabel,
    shortLabel: fallback ?? UnclassifiedLabel,
    kind: 'other',
    rank: Rank.other,
  };
}

/**
 * Re-reads a class from the label already stored on a lot, so ordering and the chain chips do not
 * have to be carried on every row. Canonical labels round-trip; an unplaced one stays unplaced.
 */
export function getClassOfLabel(label: string): ISecurityClass {
  return getSecurityClass(label);
}

function parseClass(text: string): ISecurityClass | null {
  if (PreSeedPattern.test(text)) {
    return {
      label: 'Pre-Seed',
      groupLabel: 'Pre-Seed',
      shortLabel: 'Pre-Seed',
      kind: 'preSeed',
      rank: Rank.preSeed,
    };
  }
  // The instrument decides before the round does: a "Series A SAFE" is still a SAFE, and the design
  // keeps SAFEs and notes together at the bottom rather than inside the round they anticipate.
  if (SafePattern.test(text)) {
    return { label: 'SAFE', groupLabel: 'SAFE', shortLabel: 'SAFE', kind: 'safe', rank: Rank.safe };
  }
  if (NotePattern.test(text)) {
    return {
      label: 'Convertible Note',
      // The band is plural: it holds every note, not one of them.
      groupLabel: 'Notes',
      shortLabel: 'Note',
      kind: 'note',
      rank: Rank.note,
    };
  }
  // "Series Seed" is the seed round, not a lettered series.
  if (SeedPattern.test(text)) {
    return { label: 'Seed', groupLabel: 'Seed', shortLabel: 'Seed', kind: 'seed', rank: Rank.seed };
  }

  const series = SeriesPattern.exec(text);
  if (series !== null) {
    const letter = series[1].toUpperCase();
    const tranche = series[2] === undefined ? '' : `-${Number(series[2])}`;
    const shortLabel = `${letter}${tranche}`;
    return {
      label: `Series ${shortLabel}${PreferredPattern.test(text) ? ' Preferred' : ''}`,
      // The tranche is a row within its round's band, not a band of its own.
      groupLabel: `Series ${letter}`,
      shortLabel,
      // Tranches share their round's rank, so A-1 cannot sort away from A.
      kind: 'series',
      rank: Rank.series + letterValue(letter) * 100,
    };
  }

  if (CommonPattern.test(text)) {
    return {
      label: 'Common Stock',
      groupLabel: 'Common',
      shortLabel: 'Common',
      kind: 'common',
      rank: Rank.common,
    };
  }
  return null;
}

/** A=1 … Z=26, AA=27, so a Series AA sorts after a Series Z rather than beside Series A. */
function letterValue(letters: string): number {
  return [...letters].reduce((acc, char) => acc * 26 + (char.charCodeAt(0) - 64), 0);
}
